import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// This utility helps migrate data from localStorage to Firebase
// It handles date conversion and ensures we don't lose any data

export const migrateDataToFirebase = async () => {
  const collections = ['agencies', 'articles', 'domains'];
  
  try {
    console.log('Starting data migration to Firebase...');
    
    for (const collectionName of collections) {
      // Check if we have local data
      const localKey = `firebase_${collectionName}`;
      const localData = localStorage.getItem(localKey);
      
      if (!localData) {
        console.log(`No local data found for ${collectionName}`);
        continue;
      }
      
      // Parse the local data
      const data = JSON.parse(localData);
      
      if (!data || !data.length) {
        console.log(`No items to migrate for ${collectionName}`);
        continue;
      }
      
      // Check if the collection already has data in Firebase
      const collectionRef = collection(db, collectionName);
      const existingDocs = await getDocs(collectionRef);
      
      if (!existingDocs.empty) {
        console.log(`Firebase collection ${collectionName} already has data, skipping migration`);
        continue;
      }
      
      console.log(`Migrating ${data.length} ${collectionName} to Firebase...`);
      
      // Add each document to Firebase
      for (const item of data) {
        const { id, ...itemData } = item;
        
        // Convert date strings to proper Date objects
        const processedData = {
          ...itemData,
          created_at: item.created_at ? new Date(item.created_at) : new Date(),
          updated_at: item.updated_at ? new Date(item.updated_at) : new Date(),
        };
        
        // Add published_at if it exists and is not null
        if (item.published_at) {
          processedData.published_at = new Date(item.published_at);
        }
        
        // Use setDoc to preserve the original ID
        await setDoc(doc(db, collectionName, id), processedData);
      }
      
      console.log(`Successfully migrated ${data.length} ${collectionName} to Firebase`);
    }
    
    console.log('Migration completed successfully!');
    return true;
  } catch (error) {
    console.error('Error during migration:', error);
    return false;
  }
};

// Create sample data in Firebase if collections are empty
export const createSampleDataInFirebase = async () => {
  try {
    console.log('Checking for existing data in Firebase...');
    
    // Check if agencies collection has data
    const agenciesRef = collection(db, 'agencies');
    const agenciesSnapshot = await getDocs(agenciesRef);
    
    if (!agenciesSnapshot.empty) {
      console.log('Firebase already has data, skipping sample data creation');
      return;
    }
    
    console.log('Creating sample data in Firebase...');
    
    // Sample agencies
    const agencies = [
      {
        id: '1',
        name: 'TechSEO Agency',
        contact_person: 'John Smith',
        email: 'john@techseo.com',
        phone: '+1-555-0123',
        niche_type: 'normal',
        total_articles: 50,
        total_price: 2500,
        price_per_article: 50,
        remaining_articles: 15,
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: '2',
        name: 'Digital Marketing Pro',
        contact_person: 'Sarah Johnson',
        email: 'sarah@digitalmarketing.com',
        phone: '+1-555-0456',
        niche_type: 'special',
        total_articles: 30,
        total_price: 3000,
        price_per_article: 100,
        remaining_articles: 5,
        created_at: new Date(),
        updated_at: new Date()
      }
    ];
    
    // Sample domains
    const domains = [
      {
        id: '1',
        name: 'Tech Blog',
        url: 'https://techblog.com',
        domain_authority: 65,
        niche: 'Technology',
        notes: 'Popular tech blog with high engagement',
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: '2',
        name: 'Marketing Hub',
        url: 'https://marketinghub.com',
        domain_authority: 58,
        niche: 'Marketing',
        notes: 'Marketing focused publication',
        created_at: new Date(),
        updated_at: new Date()
      }
    ];
    
    // Sample articles
    const articles = [
      {
        id: '1',
        title: 'Article for TechSEO Agency',
        agency_id: '1',
        domain_id: '1',
        status: 'published',
        price: 50,
        content: 'Sample article content...',
        keywords: 'normal',
        word_count: 1000,
        published_at: new Date(),
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: '2',
        title: 'Article for Digital Marketing Pro',
        agency_id: '2',
        domain_id: '2',
        status: 'completed',
        price: 100,
        content: 'Sample article content...',
        keywords: 'special',
        word_count: 1200,
        created_at: new Date(),
        updated_at: new Date()
      }
    ];
    
    // Add agencies to Firebase
    for (const agency of agencies) {
      const { id, ...data } = agency;
      await setDoc(doc(db, 'agencies', id), data);
    }
    
    // Add domains to Firebase
    for (const domain of domains) {
      const { id, ...data } = domain;
      await setDoc(doc(db, 'domains', id), data);
    }
    
    // Add articles to Firebase
    for (const article of articles) {
      const { id, ...data } = article;
      await setDoc(doc(db, 'articles', id), data);
    }
    
    console.log('Sample data created successfully!');
  } catch (error) {
    console.error('Error creating sample data:', error);
  }
};